<template>
  <div class="card">
    <h5 class="card-header">Question Exclusions</h5>
    <div class="card-body">
      <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
        {{ successMessage }}
        <button type="button" class="close" @click="successMessage = null" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <ErrorSummary :error="error" />
      <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="form-group">
          <label for="legalName">Interview Type</label>
          <select v-model="testId" type="text" class="form-control" style="width: 250px;" required :disabled="loadingMessage">
            <option value selected hidden disabled>Select an interview type</option>
            <option v-for="t in tests" v-bind:key="t.id" v-bind:value="t.id">
              {{ t.displayName }}
            </option>
          </select>
        </div>
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th scope="col" class="border-top-0">Code</th>
                <th scope="col" class="border-top-0">Text</th>
                <th class="text-center border-top-0 enabled-header">
                  Enabled
                  <span data-toggle="tooltip" data-placement="top" title="Enables or disables the function/district for the organization.">
                    <fa icon="question-circle" />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr colspan="12" v-if="!items.length" class="text-muted text-center">
                <td colspan="12">
                  No excluded questions
                </td>
              </tr>
              <tr v-for="item in items" :key="item.id">
                <td>
                  <span class="pt-2" :class="item.status !== 'enabled' ? 'text-muted' : ''">{{ item.code }}</span>
                </td>
                <td>
                  <span class="pt-2" :class="item.status !== 'enabled' ? 'text-muted' : ''">{{ item.displayText }}</span>
                </td>
                <td class="text-center">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="item.id"
                      :disabled="loadingMessage"
                      :checked="item.status === 'enabled'"
                      @change="toggle(item, $event)"
                    />
                    <label class="custom-control-label" :for="item.id"></label>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </form>
    </div>
    <div class="card-footer">
      <ErrorSummary :error="error" />
      <form ref="form" class="mt-3 needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="row">
          <div class="col">
            <div class="form-group">
              <select v-model="questionCode" type="text" class="form-control" required :disabled="loadingMessage">
                <option value selected hidden disabled>Select an interview question</option>
                <option v-for="tq in testQuestions" v-bind:key="tq.code" v-bind:value="tq.code">{{ tq.code }} - {{ tq.displayText }}</option>
              </select>
              <div class="invalid-feedback">Please select a question.</div>
            </div>
          </div>
          <div class="mr-3">
            <button type="button" class="btn btn-primary" @click="submit" :disabled="loadingMessage" @keyup.enter="submit">
              <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ loadingMessage ? loadingMessage : "Add" }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      successMessage: null,
      error: null,

      testId: "",
      tests: [],
      items: [],

      questionCode: "",
      testQuestions: [],
    };
  },
  created() {
    this.load();
  },
  watch: {
    organizationId() {
      this.load();
    },
    testId() {
      this.getExcludedQuestions();
      this.getTestQuestions();
    },
  },
  methods: {
    getExcludedQuestions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/questionexclusions/${this.testId}`)
        .then(res => res.data)
        .then(items => (this.items = items))
        .catch(err => (this.error = err));
    },
    getTestQuestions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/questionexclusions/${this.testId}/questions`)
        .then(res => res.data)
        .then(items => this.sortByCode(items))
        .then(items => (this.testQuestions = items))
        .catch(err => (this.error = err));
    },
    load() {
      this.loadingMessage = "Loading...";
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/tests`)
        .then(res => res.data)
        .then(items => this.sortByCode(items))
        .then(items => items.filter(item => item.status === "enabled"))
        .then(items => (this.tests = items))
        .catch(() => this.$router.push("/404"))
        .finally(() => (this.loadingMessage = null));
    },
    sortByCode(items) {
      return items.sort((a, b) => (a.code > b.code ? 1 : -1));
    },
    toggle(item, $event) {
      const payload = {
        status: $event.target.checked ? "enabled" : "disabled",
      };

      this.loadingMessage = "Saving...";
      this.$axios
        .put(`/v1/organizations/${this.organizationId}/questionexclusions/${this.testId}/exclusion/${item.id}`, payload)
        .then(() => {
          this.reset();
          this.getExcludedQuestions();
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;
      this.successMessage = null;
      this.error = null;
      this.items = [];
    },
    submit() {
      if (!this.questionCode || this.questionCode == "") return;

      const payload = {
        code: this.questionCode,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/questionexclusions/${this.testId}`, payload)
        .then(() => (this.successMessage = "Save successful!"))
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
          this.getExcludedQuestions();
          this.getTestQuestions();
        });
    },
  },
};
</script>
